import { Form, Input, Select } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { TagInput } from "@/pages/GovernancePage/components/organisms/ScmForm/components/TagInput"
import { BaseReposLabel } from "@/pages/IntegrationsPage/components/molecules/BaseReposLabel"
import {
  GCR_LOCATIONS,
  isValidGcrImagePath,
  isValidGcrLocation,
  isValidGcrProject,
  normalizeGcrImagePath,
  normalizeGcrLocation,
  stripGcrHost
} from "@/pages/IntegrationsPage/helpers/gcr"

import styles from "./FormGcr.module.scss"

import type { GcrFormValues } from "@/pages/IntegrationsPage/hooks/useGcrAPI"
import type { FormGcrProps } from "."

const PREFIX = "integrations.gcr.form"
const LOCATION_OPTIONS = GCR_LOCATIONS.map((value) => ({ value, label: value }))

export const FormGcr = (props: FormGcrProps) => {
  const { form, formId, registry, onFieldsChange, onFinish, t } = props
  const { t: tConnectGcr } = useTranslation("translation", {
    keyPrefix: "integrations.connect.gcr"
  })
  const isNew = !registry
  const initialValues: GcrFormValues = registry
    ? { repositories: registry.repositories }
    : { locations: [], repositories: [] }

  return (
    <Form
      className={styles.form}
      form={form}
      id={formId}
      layout="vertical"
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
      onFinish={onFinish}
    >
      {isNew && (
        <Form.Item
          label={t(`${PREFIX}.locations.label`)}
          name="locations"
          required={false}
          normalize={(value: string[]) =>
            Array.from(new Set((value || []).map(normalizeGcrLocation))).filter(
              Boolean
            )
          }
          rules={[
            {
              required: true,
              type: "array",
              min: 1,
              message: t(`${PREFIX}.locations.required`)
            },
            {
              validator: (_: unknown, value: string[] = []) => {
                const invalid = value.find((l) => !isValidGcrLocation(l))
                return invalid
                  ? Promise.reject(
                      new Error(
                        t(`${PREFIX}.locations.invalid`, { location: invalid })
                      )
                    )
                  : Promise.resolve()
              }
            }
          ]}
        >
          <Select
            mode="tags"
            aria-label={t(`${PREFIX}.locations.label`)}
            options={LOCATION_OPTIONS}
            tokenSeparators={[",", " "]}
            placeholder={t(`${PREFIX}.locations.placeholder`)}
          />
        </Form.Item>
      )}
      <Form.Item
        name="repositories"
        label={<BaseReposLabel t={tConnectGcr} />}
        normalize={(value: string[]) =>
          Array.from(
            new Set(
              (value || [])
                .map(normalizeGcrImagePath)
                .map((path) =>
                  registry ? stripGcrHost(path, registry.registryUrl) : path
                )
            )
          ).filter(Boolean)
        }
        rules={[
          {
            validator: (_: unknown, value: string[] = []) => {
              const invalid = value.find((path) => !isValidGcrImagePath(path))

              if (!invalid) return Promise.resolve()

              return Promise.reject(
                new Error(
                  t(`${PREFIX}.repositories.invalid`, { entry: invalid })
                )
              )
            }
          }
        ]}
      >
        <TagInput placeholder={tConnectGcr("baseReposInput.placeholder")} />
      </Form.Item>
      {isNew && (
        <Form.Item
          label={t(`${PREFIX}.projectId.label`)}
          tooltip={t(`${PREFIX}.projectId.tooltip`)}
          name="projectId"
          required={false}
          dependencies={["repositories"]}
          rules={[
            // Reads the form, not a watched value: dependencies revalidate before a watch updates.
            ({ getFieldValue }) => ({
              validator: (_: unknown, value?: string) => {
                if (value?.trim()) {
                  return isValidGcrProject(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t(`${PREFIX}.projectId.invalid`))
                      )
                }
                return getFieldValue("repositories")?.length
                  ? Promise.resolve()
                  : Promise.reject(new Error(t(`${PREFIX}.missing`)))
              }
            })
          ]}
        >
          <Input
            ref={props.focusRef}
            autoComplete="off"
            placeholder={t(`${PREFIX}.projectId.placeholder`)}
          />
        </Form.Item>
      )}
    </Form>
  )
}
